import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useUpdatePageTitle, createTitleFromSegments } from "@humansignal/core";
import { Userpic } from "@humansignal/ui";
import { SummaryStrip } from "../../components/SummaryStrip/SummaryStrip";
import { ProjectContext } from "../../providers/ProjectProvider";
import { cn } from "../../utils/bem";
import {
  BarChart,
  CHART_TOKENS,
  DIVERGING,
  Donut,
  LineChart,
  SERIES_PALETTE,
  fmtInt,
  fmtPct,
  seriesColor,
} from "../../components/charts";
import { DateRangePicker } from "../../components/charts/DateRangePicker";
import { useMetrics } from "../../hooks/useMetrics";
import { Pagination, usePage, usePageSize } from "../../components/Pagination/Pagination";
import { SettingsPageHeader } from "../../components/SettingsPageHeader/SettingsPageHeader";
import { AnalyticsGrid, AnalyticsPanel } from "../ProjectDashboard/AnalyticsPanel";
import { useTranslation } from "../../i18n/LocaleProvider";
import "./ProjectMetrics.prefix.css";

const block = cn("project-metrics");

// ── Agreement colour helper ───────────────────────────────────────────────────
// Red → amber → green across the [0,1] range. Purely for presentation.
const scoreToColor = (score) => {
  if (score == null) return CHART_TOKENS.grid;
  const clamped = Math.max(0, Math.min(1, score));
  const idx = Math.min(DIVERGING.length - 1, Math.floor(clamped * DIVERGING.length));
  return DIVERGING[idx];
};

/** The query parameters for a period chosen in the DateRangePicker. */
export const rangeToParams = (range) => {
  if (range.key === "custom" && range.from && range.to) {
    return { from: range.from, to: range.to, bucket: range.bucket };
  }
  return { range: range.key, bucket: range.bucket };
};

const seconds = (v) => (v == null ? "—" : `${Math.round(v)}s`);

/**
 * Overview: the project's figures for the period, then throughput,
 * completion and the label mix. ``extra`` is rendered inside the grid so
 * an extension's panels sit in the same columns.
 */
export const OverviewSection = ({ project, rangeParams, extra = null }) => {
  const { t } = useTranslation();

  const overview = useMetrics(
    project?.id ? "projectMetricsOverview" : null,
    project?.id ? { params: { pk: project.id } } : null,
    [project?.id],
  );
  const timeseries = useMetrics(
    project?.id ? "projectMetricsTimeseries" : null,
    project?.id ? { params: { pk: project.id, ...rangeParams } } : null,
    [project?.id, rangeParams],
  );
  // Open review-queue backlog, so reviewers see it without opening that section.
  const reviewQueue = useMetrics(
    project?.id ? "projectReviewQueue" : null,
    project?.id ? { params: { pk: project.id, status: "open", page_size: 1 }, suppressError: true } : null,
    [project?.id],
  );
  const openReview = reviewQueue.data ? (reviewQueue.data.status_counts?.open ?? reviewQueue.data.count ?? 0) : null;

  if (!project?.id) return null;

  // Treat unexpected response shapes (e.g. a ``{detail: 'Not found'}``
  // payload when the user lacks permission) as "no data" so downstream
  // reads like ``ov.tasks.labeled`` stay safe.
  const ov = overview.data && typeof overview.data === "object" && overview.data.tasks ? overview.data : null;
  const ts = timeseries.data && Array.isArray(timeseries.data.annotations) ? timeseries.data : null;

  // Completion is measured against the total annotations a project actually
  // requires (sum of per-task overlap): 100% means every required annotation
  // has been produced, not just every task having at least one.
  const annotationsRequired = ov?.tasks?.annotations_required ?? 0;
  const annotationsDoneCapped = ov?.tasks?.annotations_done_capped ?? 0;
  const completionPct = annotationsRequired > 0 ? Math.round((annotationsDoneCapped / annotationsRequired) * 100) : 0;

  const labelDist = (ov?.label_distribution ?? []).map((d, i) => ({
    label: d.label,
    value: d.value,
    color: seriesColor(i),
  }));

  return (
    <>
      <SummaryStrip
        items={[
          {
            label: "Complete",
            value: `${completionPct}%`,
            sub: ov?.tasks
              ? `${fmtInt(annotationsDoneCapped)} of ${fmtInt(annotationsRequired)} annotations, ${fmtInt(ov.tasks.labeled)} of ${fmtInt(ov.tasks.total)} tasks`
              : undefined,
          },
          {
            label: "Annotations",
            value: ov?.annotations?.total != null ? fmtInt(ov.annotations.total) : "—",
            sub: ts?.compare?.previous != null ? `vs ${fmtInt(ts.compare.previous)} the period before` : undefined,
          },
          {
            label: "Active annotators",
            value: ov?.annotators?.active != null ? fmtInt(ov.annotators.active) : "—",
            sub: "with 1 or more annotations",
          },
          {
            label: "Average lead time",
            value: seconds(ov?.avg_lead_time),
            sub: ov?.avg_lead_time == null ? "not recorded yet" : "per annotation",
          },
          {
            label: "Average agreement",
            value: ov?.agreement?.mean == null ? "—" : fmtPct(ov.agreement.mean),
            sub: ov?.agreement?.count
              ? `${fmtInt(ov.agreement.count)} scored task${ov.agreement.count === 1 ? "" : "s"}, ${ov.agreement.method}`
              : "not enough overlap yet",
          },
          {
            label: "Review queue",
            value: openReview == null ? "—" : fmtInt(openReview),
            sub: openReview === 0 ? "nothing open" : "open items",
          },
        ]}
      />

      <AnalyticsGrid>
        <AnalyticsPanel
          title={t("pages.settings.project_metrics.annotations_over_time")}
          subtitle={t("pages.settings.project_metrics.counts_per_day_or_week_for_longer_ranges")}
          empty={!ts || ts.annotations.length === 0 ? "No activity in this period." : null}
        >
          {ts && ts.annotations.length > 0 && (
            <LineChart
              data={ts.annotations}
              height={220}
              color={SERIES_PALETTE[0]}
              valueFormatter={fmtInt}
              seriesNames={["Annotations"]}
            />
          )}
        </AnalyticsPanel>

        <AnalyticsPanel
          title={t("pages.settings.project_metrics.completion")}
          subtitle={`${fmtInt(annotationsDoneCapped)} of ${fmtInt(annotationsRequired)} annotations, ${fmtInt(ov?.tasks?.labeled ?? 0)} of ${fmtInt(ov?.tasks?.total ?? 0)} tasks labelled.`}
        >
          <div className={block.elem("donut-wrap").toClassName()}>
            <Donut
              value={annotationsDoneCapped}
              total={annotationsRequired}
              color={SERIES_PALETTE[1]}
              size={160}
              thickness={18}
            />
            <ul className={block.elem("legend").toClassName()}>
              <li>
                <span className={block.elem("dot").toClassName()} style={{ background: SERIES_PALETTE[1] }} />
                {t("pages.settings.project_metrics.done")} <b>{fmtInt(annotationsDoneCapped)}</b>
              </li>
              <li>
                <span className={block.elem("dot").toClassName()} style={{ background: CHART_TOKENS.grid }} />
                {t("pages.settings.project_metrics.remaining")}{" "}
                <b>{fmtInt(Math.max(0, annotationsRequired - annotationsDoneCapped))}</b>
              </li>
              <li>
                <span className={block.elem("dot").toClassName()} style={{ background: "transparent" }} />
                {t("pages.settings.project_metrics.tasks_labeled")}{" "}
                <b>
                  {fmtInt(ov?.tasks?.labeled ?? 0)} / {fmtInt(ov?.tasks?.total ?? 0)}
                </b>
              </li>
            </ul>
          </div>
        </AnalyticsPanel>

        <AnalyticsPanel
          title={t("pages.settings.project_metrics.label_distribution")}
          subtitle={t("pages.settings.project_metrics.how_often_each_label_appears_across_anno")}
          empty={labelDist.length === 0 ? "No label yet." : null}
          span={extra ? 1 : 2}
        >
          {labelDist.length > 0 && (
            /* Bars sized to the card rather than to a 28px row, so three
               labels do not sit in the top third of it. */
            <BarChart
              data={labelDist}
              orientation="horizontal"
              valueFormatter={fmtInt}
              height={Math.min(220, Math.max(150, labelDist.length * 40 + 30))}
            />
          )}
        </AnalyticsPanel>

        {extra}
      </AnalyticsGrid>
    </>
  );
};

/** People: who annotated in the period, and how much. */
export const PeopleSection = ({ project, rangeParams, extra = null }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [annPage, setAnnPage] = usePage("ap", 1);
  const [annPageSize, setAnnPageSize] = usePageSize("ap_size", 10);

  // The rows are period-filtered: back to the first page whenever the
  // period changes so the view is not stranded on a page that is gone.
  useEffect(() => {
    setAnnPage(1);
  }, [rangeParams]);

  const overview = useMetrics(
    project?.id ? "projectMetricsOverview" : null,
    project?.id ? { params: { pk: project.id } } : null,
    [project?.id],
  );
  const annotators = useMetrics(
    project?.id ? "projectMetricsAnnotators" : null,
    project?.id ? { params: { pk: project.id, ...rangeParams } } : null,
    [project?.id, rangeParams],
  );

  if (!project?.id) return null;

  const ov = overview.data && typeof overview.data === "object" && overview.data.tasks ? overview.data : null;
  const rows = annotators.data?.annotators ?? [];
  const total = rows.reduce((s, r) => s + (r.annotations || 0), 0);
  const mean = (key) => {
    const vals = rows.map((r) => r[key]).filter((v) => v != null);
    return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
  };
  const leadTime = mean("avg_lead_time");
  const agreement = mean("avg_agreement");

  // Per-annotator progress: the denominator is the per-annotator task
  // limit when the project has one, otherwise the total tasks uploaded.
  const denom =
    ov?.tasks?.tasks_per_annotator_limit ?? ov?.tasks?.total ?? (Math.max(...rows.map((r) => r.annotations || 0)) || 1);

  return (
    <>
      <SummaryStrip
        items={[
          { label: "Active annotators", value: fmtInt(rows.length), sub: "in this period" },
          {
            label: "Annotations",
            value: fmtInt(total),
            sub: rows.length ? `${fmtInt(Math.round(total / rows.length))} per person` : undefined,
          },
          {
            label: "Average lead time",
            value: seconds(leadTime),
            sub: leadTime == null ? "not recorded yet" : "per annotation",
          },
          {
            label: "Average agreement",
            value: agreement == null ? "—" : fmtPct(agreement),
            sub: agreement == null ? "not enough overlap yet" : "across annotators",
          },
        ]}
      />

      <AnalyticsGrid>
        <AnalyticsPanel
          title={t("pages.settings.project_metrics.contributors")}
          subtitle={`${rows.length} annotator${rows.length === 1 ? "" : "s"} in this period, most annotations first.`}
          span={2}
          empty={rows.length === 0 ? "Nobody annotated in this period." : null}
        >
          {rows.length > 0 && (
            <>
              <table className={block.elem("table").toClassName()}>
                <thead>
                  <tr>
                    <th>{t("pages.settings.project_metrics.annotator")}</th>
                    <th style={{ textAlign: "right" }}>{t("pages.settings.project_metrics.annotations")}</th>
                    <th style={{ textAlign: "right" }}>{t("pages.settings.project_metrics.avg_lead_time")}</th>
                    <th style={{ textAlign: "right" }}>{t("pages.settings.project_metrics.avg_agreement")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.slice((annPage - 1) * annPageSize, annPage * annPageSize).map((row) => {
                    const pct = Math.min(100, Math.round(((row.annotations || 0) / denom) * 100));
                    return (
                      <tr
                        key={row.user_id}
                        onClick={() => row.user_id && history.push(`/users/${row.user_id}`)}
                        style={{ cursor: row.user_id ? "pointer" : "default" }}
                      >
                        <td>
                          <div className={block.elem("user-cell").toClassName()}>
                            <Userpic
                              user={{
                                id: row.user_id,
                                first_name: row.name?.split(" ")[0],
                                last_name: row.name?.split(" ").slice(1).join(" "),
                                email: row.email,
                              }}
                              size={28}
                            />
                            <div>
                              <div className={block.elem("name").toClassName()}>{row.name}</div>
                              <div className={block.elem("email").toClassName()}>{row.email}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                          <div className={block.elem("bar-cell").toClassName()}>
                            <span className={block.elem("bar-track").toClassName()}>
                              <span className={block.elem("bar-fill").toClassName()} style={{ width: `${pct}%` }} />
                            </span>
                            <span className={block.elem("bar-value").toClassName()}>{fmtInt(row.annotations)}</span>
                          </div>
                        </td>
                        <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                          {seconds(row.avg_lead_time)}
                        </td>
                        <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                          {row.avg_agreement == null ? (
                            "—"
                          ) : (
                            <span
                              className={block.elem("score-chip").toClassName()}
                              style={{ background: scoreToColor(row.avg_agreement) }}
                            >
                              {fmtPct(row.avg_agreement)}
                            </span>
                          )}
                        </td>
                        <td />
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <Pagination
                name="project-metrics-contributors"
                page={annPage}
                pageSize={annPageSize}
                totalItems={rows.length}
                urlParamName="ap"
                pageSizeOptions={[10, 25, 50, 100]}
                onPageLoad={(p, ps) => {
                  setAnnPage(p);
                  setAnnPageSize(ps);
                }}
                style={{ paddingTop: 12 }}
              />
            </>
          )}
        </AnalyticsPanel>

        {extra}
      </AnalyticsGrid>
    </>
  );
};

/**
 * The stand-alone Metrics page (and the legacy metrics modal): the
 * overview and the people, with their own period picker. The project's
 * Analytics page composes the sections itself.
 */
export const ProjectMetrics = ({ embedded = false }) => {
  const { t } = useTranslation();
  const { project } = useContext(ProjectContext);
  useUpdatePageTitle(createTitleFromSegments([project?.title, "Metrics"]));

  const [range, setRange] = useState({ key: "30d", days: 30, bucket: "day" });
  const rangeParams = useMemo(() => rangeToParams(range), [range]);

  if (!project?.id) return null;

  return (
    <div className={block.toClassName()}>
      <div
        className={`${block.elem("wrapper").toClassName()} ${cn("settings-page").mod({ wide: true }).toClassName()}`}
      >
        {!embedded ? (
          <SettingsPageHeader
            title={t("pages.settings.project_metrics.metrics")}
            subtitle={t("pages.settings.project_metrics.throughput_quality_and_contributor_activ")}
            actions={<DateRangePicker value={range.key} custom onChange={setRange} />}
          />
        ) : (
          <div className={block.elem("range-row").toClassName()}>
            <DateRangePicker value={range.key} custom onChange={setRange} />
          </div>
        )}
        <OverviewSection project={project} rangeParams={rangeParams} />
        <PeopleSection project={project} rangeParams={rangeParams} />
      </div>
    </div>
  );
};

ProjectMetrics.title = "Metrics";
ProjectMetrics.path = "/metrics";
